import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles } from "lucide-react";
import { useNavigate } from "react-router-dom";

const phrases = [
  "Test Automation",
  "Bug Detection",
  "Smart Reporting",
  "Quality Assurance",
];

const Hero = () => {
  const [currentPhrase, setCurrentPhrase] = useState("");
  const [phraseIndex, setPhraseIndex] = useState(0);
  const [charIndex, setCharIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const target = phrases[phraseIndex];
    const timeout = setTimeout(() => {
      if (!isDeleting) {
        setCurrentPhrase(target.slice(0, charIndex + 1));
        setCharIndex((prev) => prev + 1);
        if (charIndex + 1 === target.length) {
          setTimeout(() => setIsDeleting(true), 1800);
        }
      } else {
        setCurrentPhrase(target.slice(0, charIndex - 1));
        setCharIndex((prev) => prev - 1);
        if (charIndex - 1 === 0) {
          setIsDeleting(false);
          setPhraseIndex((prev) => (prev + 1) % phrases.length);
        }
      }
    }, isDeleting ? 40 : 80);

    return () => clearTimeout(timeout);
  }, [charIndex, isDeleting, phraseIndex]);

  const goToSignup = () => navigate("/signup");

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background layers */}
      <div className="absolute inset-0">
        <img
          src="/hero-bg.jpg"
          alt=""
          className="w-full h-full object-cover opacity-20 contrast-[1.1]"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-background/40 via-background/60 to-background" />
        <div className="absolute inset-0 grid-pattern opacity-[0.15]" />
        {/* Multi-layer ambient glow - soft blue for light mode */}
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[500px] bg-primary/10 rounded-full blur-[120px]" />
        <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[300px] bg-blue-400/5 rounded-full blur-[100px]" />
      </div>

      <div className="container mx-auto px-4 relative z-10 text-center pt-20">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <span className="inline-flex items-center gap-2.5 text-[11px] font-semibold tracking-[0.2em] uppercase text-primary/90 mb-10 border border-primary/15 rounded-full px-5 py-2.5 bg-primary/5 backdrop-blur-sm">
            <Sparkles size={11} className="text-primary" />
            Powered by Alternative Path
          </span>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="text-5xl md:text-7xl lg:text-[5.5rem] font-display font-bold leading-[1.02] mb-8 max-w-5xl mx-auto tracking-[-0.02em]"
        >
          <span className="text-gradient">AI-Powered</span>
          <br />
          <span className="inline-flex items-center min-h-[1.1em]">
            {currentPhrase}
            <span className="ml-1 w-[3px] h-[0.75em] bg-primary animate-pulse inline-block rounded-full" />
          </span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto mb-14 leading-relaxed font-light"
        >
          From automated test creation to real-time analytics — QA Path uses AI to eliminate manual effort, catch bugs earlier, and
          deliver release-ready confidence at every sprint.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.3 }}
          className="flex flex-col sm:flex-row items-center justify-center gap-4"
        >
          <button
            type="button"
            onClick={goToSignup}
            className="group inline-flex items-center gap-2.5 bg-primary text-primary-foreground font-semibold px-8 py-4 rounded-xl shadow-lg shadow-primary/25 hover:shadow-xl hover:shadow-primary/35 transition-all duration-500 text-sm"
          >
            Start Free Trial
            <ArrowRight
              size={15}
              className="group-hover:translate-x-1 transition-transform duration-300"
            />
          </button>
        </motion.div>

        {/* Trust indicators */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.7 }}
          className="mt-24 flex items-center justify-center gap-10 text-xs text-muted-foreground/80"
        >
          {["99.9% Uptime", "SOC 2 Compliant", "Enterprise Ready"].map(
            (label, i) => (
              <span
                key={label}
                className={`flex items-center gap-2.5 ${
                  i === 1 ? "hidden sm:flex" : "flex"
                }`}
              >
                <span className="w-1.5 h-1.5 rounded-full bg-primary/60" />
                {label}
              </span>
            )
          )}
        </motion.div>
      </div>

      {/* Bottom fade */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-background to-transparent" />
    </section>
  );
};

export default Hero;

